import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Plus, CheckCircle, Circle, Trash2, X, Loader2,
  Video, Image, FileText, Calendar, Smartphone, Settings, Users, Headphones,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORY_CONFIG = {
  video: { label: 'Vidéo', icon: Video, color: 'bg-red-500' },
  post: { label: 'Post', icon: Image, color: 'bg-pink-500' },
  story: { label: 'Story', icon: FileText, color: 'bg-purple-500' },
  rdv: { label: 'RDV', icon: Calendar, color: 'bg-blue-500' },
  application: { label: 'Application', icon: Smartphone, color: 'bg-green-500' },
  crm: { label: 'CRM', icon: Settings, color: 'bg-gray-500' },
  demande_client: { label: 'Demande client', icon: Users, color: 'bg-amber-500' },
  sav: { label: 'SAV', icon: Headphones, color: 'bg-orange-500' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', color: 'text-green-500' },
  medium: { label: 'Moyenne', color: 'text-amber-500' },
  high: { label: 'Haute', color: 'text-red-500' },
};

const CRMTasks = () => {
  const { token } = useAuth();
  const { darkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'demande_client',
    priority: 'medium',
    dueDate: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setTasks(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/crm/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
        })
      });

      if (response.ok) {
        fetchTasks();
        setShowModal(false);
        setFormData({
          title: '',
          description: '',
          category: 'demande_client',
          priority: 'medium',
          dueDate: ''
        });
      }
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const toggleTask = async (id) => {
    try {
      await fetch(`${API_URL}/api/crm/tasks/${id}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const deleteTask = async (id) => {
    try {
      await fetch(`${API_URL}/api/crm/tasks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (selectedCategory !== 'all' && task.category !== selectedCategory) return false;
    if (!showCompleted && task.completed) return false;
    return true;
  });

  const tasksByCategory = filteredTasks.reduce((acc, task) => {
    if (!acc[task.category]) acc[task.category] = [];
    acc[task.category].push(task);
    return acc;
  }, {});

  const pendingCount = tasks.filter(t => !t.completed).length;
  const completedCount = tasks.filter(t => t.completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-inter font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            To-Do List
          </h1>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {pendingCount} tâche{pendingCount > 1 ? 's' : ''} en cours • {completedCount} terminée{completedCount > 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          onClick={() => setShowModal(true)}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouvelle tâche
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Filter className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm ${
              darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'
            }`}
          >
            <option value="all">Toutes les catégories</option>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>
        <label className={`flex items-center gap-2 cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="rounded"
          />
          Afficher les tâches terminées
        </label>
      </div>

      {/* Category buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
              : darkMode 
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Tout ({tasks.filter(t => !showCompleted ? !t.completed : true).length})
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
          const count = tasks.filter(t => t.category === key && (!showCompleted ? !t.completed : true)).length;
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedCategory === key
                  ? `${config.color} text-white`
                  : darkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Tasks by category */}
      {selectedCategory === 'all' ? (
        <div className="space-y-6">
          {Object.entries(tasksByCategory).map(([category, categoryTasks]) => {
            const config = CATEGORY_CONFIG[category];
            const Icon = config?.icon || FileText;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config?.color || 'bg-gray-500'}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <h2 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {config?.label || category}
                  </h2>
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    ({categoryTasks.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {categoryTasks.map(task => (
                    <TaskItem key={task.id} task={task} darkMode={darkMode} onToggle={toggleTask} onDelete={deleteTask} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map(task => (
            <TaskItem key={task.id} task={task} darkMode={darkMode} onToggle={toggleTask} onDelete={deleteTask} />
          ))}
        </div>
      )}

      {filteredTasks.length === 0 && (
        <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune tâche {!showCompleted ? 'en cours' : ''}</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className={`w-full max-w-md ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className={darkMode ? 'text-white' : ''}>Nouvelle tâche</CardTitle>
              <button onClick={() => setShowModal(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </CardHeader>
            <CardContent>
              <form onSubmit={createTask} className="space-y-4">
                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Titre *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>

                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className={`w-full rounded-md border px-3 py-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Catégorie
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className={`w-full mt-1 rounded-md border px-3 py-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    >
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Priorité
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className={`w-full mt-1 rounded-md border px-3 py-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    >
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Date d'échéance
                  </label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                    className={darkMode ? 'bg-gray-700 border-gray-600 text-white' : ''}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                    Créer
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Task Item Component
const TaskItem = ({ task, darkMode, onToggle, onDelete }) => {
  const config = CATEGORY_CONFIG[task.category];
  const priorityConfig = PRIORITY_CONFIG[task.priority];
  const Icon = config?.icon || FileText;

  return (
    <div className={`p-4 rounded-lg border transition-all ${
      task.completed 
        ? darkMode ? 'bg-gray-800/50 border-gray-700 opacity-60' : 'bg-gray-50 border-gray-200 opacity-60'
        : darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(task.id)}
          className={`mt-1 ${task.completed ? 'text-green-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}
        >
          {task.completed ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${task.completed ? 'line-through' : ''} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {task.title}
            </span>
            <span className={`text-xs ${priorityConfig?.color}`}>
              • {priorityConfig?.label}
            </span>
          </div>
          
          {task.description && (
            <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {task.description}
            </p>
          )}
          
          <div className="flex items-center gap-3 text-xs">
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-white ${config?.color || 'bg-gray-500'}`}>
              <Icon className="w-3 h-3" />
              {config?.label || task.category}
            </span>
            {task.dueDate && (
              <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                Échéance: {new Date(task.dueDate).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => onDelete(task.id)}
          className={`p-2 rounded-lg text-red-500 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CRMTasks;
